import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { InstallationCheckResult } from '../types';
import { setAppBridgeInstance } from '../services/api';

// Error types for better error handling
class AppBridgeError extends Error {
  constructor(message: string, public code: string) {
    super(message);
    this.name = 'AppBridgeError';
  }
}

// AppBridge type definitions
interface AppBridgeInstance {
  getSessionToken: () => Promise<string>;
  invoke: (method: string, params: any) => Promise<any>;
}

interface AppBridgeContextType {
  isReady: boolean;
  isInstalled: boolean;
  isLoading: boolean;
  error: string | null;
  handle: string | null;
  appBridge: AppBridgeInstance | null;
  retry: () => void;
}

const AppBridgeContext = createContext<AppBridgeContextType | null>(null);

interface AppBridgeProviderProps {
  children: ReactNode;
}

// API client for installation check
const checkInstallationApi = async (
  handle: string,
  sessionToken: string
): Promise<InstallationCheckResult> => {
  const response = await fetch(
    `/api/v1/auth/check-installation`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionToken}`,
      },
      body: JSON.stringify({ handle }),
    }
  );

  if (!response.ok) {
    throw new AppBridgeError(
      `HTTP error! status: ${response.status}`,
      'HTTP_ERROR'
    );
  }

  const responseText = await response.text();
  try {
    return JSON.parse(responseText);
  } catch {
    throw new AppBridgeError('服务器返回了无效的数据格式', 'PARSE_ERROR');
  }
};

export function AppBridgeProvider({ children }: AppBridgeProviderProps) {
  const [state, setState] = useState({
    isReady: false,
    isInstalled: false,
    isLoading: true,
    error: null as string | null,
    handle: null as string | null,
    appBridge: null as AppBridgeInstance | null,
  });

  const [retryCount, setRetryCount] = useState(0);

  // Initialize AppBridge
  useEffect(() => {
    const initAppBridge = async () => {
      try {
        const AppBridgeModule = await import('@shoplinedev/appbridge');
        const { default: Client, shared, Oauth } = AppBridgeModule;

        const urlParams = new URLSearchParams(window.location.search);
        const handleFromUrl = urlParams.get('handle');

        if (!handleFromUrl) {
          throw new AppBridgeError('缺少店铺标识参数', 'MISSING_HANDLE');
        }

        const bridge = Client.createApp({
          appKey: import.meta.env.VITE_SHOPLINE_APP_KEY || '',
          host: `${handleFromUrl}.myshopline.com`,
        });

        const oauthInstance = Oauth.create(bridge);

        const appBridgeInstance: AppBridgeInstance = {
          getSessionToken: () => shared.getSessionToken(bridge),
          invoke: async (method: string, params: any) => {
            if (method === 'Oauth') {
              return oauthInstance.invoke(params);
            }
            throw new AppBridgeError(`Unknown method: ${method}`, 'UNKNOWN_METHOD');
          },
        };

        setState((prev) => ({
          ...prev,
          handle: handleFromUrl,
          appBridge: appBridgeInstance,
          isReady: true,
        }));
        setAppBridgeInstance(appBridgeInstance);
      } catch (err) {
        const errorMessage = err instanceof AppBridgeError 
          ? err.message 
          : err instanceof Error
            ? err.message
            : 'AppBridge 初始化失败';
        
        setState((prev) => ({
          ...prev,
          error: errorMessage,
          isLoading: false,
        }));
      }
    };

    initAppBridge();
  }, [retryCount]);

  // Check installation status
  const checkInstallation = useCallback(async () => {
    if (!state.isReady || !state.handle || !state.appBridge) return;

    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      const sessionToken = await state.appBridge.getSessionToken();
      
      if (!sessionToken) {
        throw new AppBridgeError('无法获取 sessionToken', 'NO_SESSION_TOKEN');
      }

      const result = await checkInstallationApi(state.handle, sessionToken);

      if (!result.success) {
        throw new AppBridgeError(
          result.message || '检测安装状态失败',
          'CHECK_FAILED'
        );
      }

      if (result.installed) {
        setState((prev) => ({
          ...prev,
          isInstalled: true,
          isLoading: false,
        }));
      } else if (result.authParams) {
        // Auto-authorize if not installed
        await state.appBridge.invoke('Oauth', result.authParams);
      } else {
        throw new AppBridgeError('授权参数未准备好', 'NO_AUTH_PARAMS');
      }
    } catch (err) {
      const errorMessage = err instanceof AppBridgeError
        ? err.message
        : err instanceof Error 
          ? err.message 
          : '检测安装状态失败';
      
      setState((prev) => ({ ...prev, error: errorMessage, isLoading: false }));
    }
  }, [state.isReady, state.handle, state.appBridge]);

  // Check installation when ready
  useEffect(() => {
    checkInstallation();
  }, [checkInstallation]);

  // Retry handler
  const retry = useCallback(() => {
    setRetryCount((prev) => prev + 1);
    setState((prev) => ({
      ...prev,
      error: null,
      isLoading: true,
    }));
  }, []);

  // Loading state component
  if (state.isLoading) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        加载中...
      </div>
    );
  }

  // Error state component
  if (state.error) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div style={{ color: '#ff4d4f', fontSize: 18 }}>出错了</div>
        <div>{state.error}</div>
        <button
          onClick={retry}
          style={{
            padding: '8px 16px',
            backgroundColor: '#1890ff',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
          }}
        >
          重试
        </button>
      </div>
    );
  }

  return (
    <AppBridgeContext.Provider
      value={{
        isReady: state.isReady,
        isInstalled: state.isInstalled,
        isLoading: state.isLoading,
        error: state.error,
        handle: state.handle,
        appBridge: state.appBridge,
        retry,
      }}
    >
      {children}
    </AppBridgeContext.Provider>
  );
}

export function useAppBridge() {
  const context = useContext(AppBridgeContext);
  if (!context) {
    throw new Error('useAppBridge must be used within an AppBridgeProvider');
  }
  return context;
}
